import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { CalendarRange, ListOrdered, Building2, CalendarClock } from 'lucide-react';
import { format, differenceInCalendarDays, parseISO, startOfMonth, addMonths, isValid } from 'date-fns';

const WINDOW_OPTIONS = [
  { value: '60', label: 'Next 60 days' },
  { value: '90', label: 'Next 90 days' },
  { value: '180', label: 'Next 6 months' },
  { value: '365', label: 'Next 12 months' },
  { value: 'all', label: 'All upcoming' },
];

const safeParse = (s) => {
  if (!s) return null;
  try {
    const d = typeof s === 'string' && s.includes('T') ? new Date(s) : parseISO(s);
    return isValid(d) ? d : null;
  } catch { return null; }
};

const daysBadge = (days) => {
  if (days < 0) return { label: 'Expired', tone: 'red' };
  if (days <= 15) return { label: `${days}d`, tone: 'red' };
  if (days <= 30) return { label: `${days}d`, tone: 'amber' };
  if (days <= 90) return { label: `${days}d`, tone: 'orange' };
  return { label: `${days}d`, tone: 'muted' };
};

const toneClasses = (tone) => ({
  red: 'bg-red-500/10 text-red-600 border-red-500/30',
  amber: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  orange: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  muted: 'bg-muted/40 text-muted-foreground border-border',
}[tone] || 'bg-muted/40 text-muted-foreground border-border');

export const AvailableFromView = ({ units, venues = [], showVenue = false }) => {
  const [windowDays, setWindowDays] = useState('365');
  const [mode, setMode] = useState('list'); // 'list' | 'calendar'
  // Freeze "now" at mount so useMemo cache actually holds across renders.
  const [now] = useState(() => new Date());

  const rows = useMemo(() => {
    const out = [];
    for (const u of units) {
      if (u.status !== 'sold') continue;
      const expiry = safeParse(u.lockin_expiry_date);
      if (!expiry) continue;
      const days = differenceInCalendarDays(expiry, now);
      if (windowDays !== 'all' && days > parseInt(windowDays)) continue;
      out.push({ ...u, _expiry: expiry, _days: days });
    }
    out.sort((a, b) => a._expiry - b._expiry);
    return out;
  }, [units, windowDays, now]);

  const venueName = (id) => venues.find(v => v.id === id)?.name || '';

  // Group rows by "MMM yyyy" for the list view
  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      const key = format(r._expiry, 'MMM yyyy');
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(r);
    }
    return Array.from(g.entries());
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="available-from-view">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="w-4 h-4 text-muted-foreground" />
        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="w-48" data-testid="available-from-window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto inline-flex rounded-md border border-border overflow-hidden">
          <Button
            variant={mode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setMode('list')}
            className="rounded-none"
            data-testid="available-from-mode-list"
          >
            <ListOrdered className="w-3 h-3 mr-1" /> List
          </Button>
          <Button
            variant={mode === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setMode('calendar')}
            className="rounded-none"
            data-testid="available-from-mode-calendar"
          >
            <CalendarRange className="w-3 h-3 mr-1" /> Calendar
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No units expected to free up in this window. Widen the window or check back later.
        </Card>
      ) : mode === 'list' ? (
        <ListView grouped={grouped} venueName={venueName} showVenue={showVenue} />
      ) : (
        <CalendarView rows={rows} venueName={venueName} showVenue={showVenue} now={now} />
      )}
    </div>
  );
};

// ---------- List view ---------- //
const ListView = ({ grouped, venueName, showVenue }) => (
  <div className="space-y-4" data-testid="available-from-list">
    {grouped.map(([month, items]) => (
      <div key={month} className="space-y-2">
        <div className="flex items-baseline gap-2">
          <h4 className="font-heading font-semibold text-base">{month}</h4>
          <span className="text-xs text-muted-foreground">{items.length} unit{items.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="space-y-2">
          {items.map(u => {
            const badge = daysBadge(u._days);
            return (
              <Card key={u.id} className="p-3 flex items-center gap-3" data-testid={`available-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-sm truncate">{u.name}</span>
                    <Badge variant="outline" className="text-[10px]">{u.category}</Badge>
                    {u.floor && <Badge variant="secondary" className="text-[10px]">{u.floor}</Badge>}
                    {showVenue && venueName(u.venue_id) && (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {venueName(u.venue_id)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {u.client_name || '—'} · {u.total_seats} seats · Lock-in expires {format(u._expiry, 'PPP')}
                    {safeParse(u.lease_expiry_date) ? ` · Lease ends ${format(safeParse(u.lease_expiry_date), 'PPP')}` : ''}
                    {u.client_note ? ` · "${u.client_note}"` : ''}
                  </p>
                </div>
                <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${toneClasses(badge.tone)}`}>
                  {badge.label}
                </span>
              </Card>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

// ---------- Calendar view (month grid: current month + next 11) ---------- //
const CalendarView = ({ rows, venueName, showVenue, now }) => {
  // Build a 12-month window starting from the current month
  const months = Array.from({ length: 12 }, (_, i) => startOfMonth(addMonths(now, i)));
  const byMonth = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = format(startOfMonth(r._expiry), 'yyyy-MM');
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return m;
  }, [rows]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="available-from-calendar">
      {months.map(m => {
        const key = format(m, 'yyyy-MM');
        const items = byMonth.get(key) || [];
        return (
          <Card key={key} className={`p-3 space-y-2 ${items.length ? '' : 'opacity-60'}`} data-testid={`calendar-cell-${key}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-semibold text-sm">{format(m, 'MMM yyyy')}</span>
              {items.length > 0 && (
                <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
              )}
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">—</p>
            ) : (
              <div className="space-y-1">
                {items.slice(0, 5).map(u => (
                  <div key={u.id} className="text-xs truncate">
                    <span className="font-medium">{u.name}</span>
                    {u.floor && <span className="text-muted-foreground"> · {u.floor}</span>}
                    <span className="text-muted-foreground"> · {u.total_seats}s</span>
                    {showVenue && venueName(u.venue_id) && (
                      <span className="text-muted-foreground"> · {venueName(u.venue_id).slice(0, 8)}</span>
                    )}
                  </div>
                ))}
                {items.length > 5 && (
                  <p className="text-[10px] text-muted-foreground">+{items.length - 5} more</p>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
